//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled, { css } from 'styled-components';

import { getButtonStyle } from '@components/atoms/button/Button';
import {
  FlatDefaultButtonStyles,
  FlatPrimaryButtonStyles,
  IButtonStateStyles,
  PrimaryButtonStyles,
  SecondaryButtonStyles,
  TertiaryButtonStyles,
} from '@components/atoms/button/buttonStyles';
import { Mixins } from '@theme/mixins';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface Props
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    ITrackableComponent {
  isDisabled?: boolean;
}

/**
 * The Anchor atom renders an HTML anchor element with secure handling for the target attribute.
 * Use the prestyled `PrimaryAnchor` and `BodyPrimaryAnchor` atoms to easily use our standardized styles.
 */
export const Anchor = ({
  href,
  isDisabled = false,
  target,
  ...anchorProps
}: Props) => {
  return (
    <TrackableAnchor
      {...anchorProps}
      target={target}
      {...(target &&
        target !== '_parent' &&
        target !== '_self' &&
        target !== '_top' && {
          rel: 'noopener',
        })}
      {...(isDisabled && { 'aria-disable': true, role: 'link' })}
      href={isDisabled ? undefined : href}
    />
  );
};

const TrackableAnchor = withTracking(styled.a``);

export const PrimaryAnchor = styled(Anchor)`
  ${Mixins.Text.LinkPrimary};
`;

export const BodyPrimaryAnchor = styled(Anchor)`
  ${Mixins.Text.LinkBodyPrimary};
`;

export const SecondaryAnchor = styled(Anchor)`
  ${Mixins.Text.LinkBodySecondary};
`;

export const CaptionPrimaryAnchor = styled(Anchor)`
  ${Mixins.Text.LinkCaptionPrimary};
`;

export const ButtonStyleAnchor = styled(Anchor)<{
  isDisabled?: boolean;
  buttonStyles: IButtonStateStyles;
}>(
  ({ buttonStyles, isDisabled, theme }) => css`
    ${getButtonStyle({ theme, buttonStyles, isDisabled })}

    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${theme.fontWeights.semiBold};
    padding: ${theme.spacings[2]} ${theme.spacings[3]};

    && {
      text-decoration: none;
      color: ${buttonStyles.standard.color};
    }

    &&:hover {
      color: ${buttonStyles.hover.color};
      text-decoration: none;
    }

    ${isDisabled &&
    `
      && {
        color: ${buttonStyles.disabled.color}; /* needed because disabled color might be overridden in anchor element */
        cursor: not-allowed;
      }
    `}
  `,
);

export const PrimaryButtonStyleAnchor = (
  props: React.ComponentProps<typeof Anchor>,
) => <ButtonStyleAnchor buttonStyles={PrimaryButtonStyles} {...props} />;

export const SecondaryButtonStyleAnchor = (
  props: React.ComponentProps<typeof Anchor>,
) => <ButtonStyleAnchor buttonStyles={SecondaryButtonStyles} {...props} />;

export const FlatPrimaryButtonStyleAnchor = (
  props: React.ComponentProps<typeof Anchor>,
) => <ButtonStyleAnchor buttonStyles={FlatPrimaryButtonStyles} {...props} />;

export const FlatDefaultButtonStyleAnchor = (
  props: React.ComponentProps<typeof Anchor>,
) => <ButtonStyleAnchor buttonStyles={FlatDefaultButtonStyles} {...props} />;

export const TertiaryButtonStyleAnchor = (
  props: React.ComponentProps<typeof Anchor>,
) => <ButtonStyleAnchor buttonStyles={TertiaryButtonStyles} {...props} />;

export default Anchor;
